import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { InstrumentCategories, InstrumentChartCardInput } from '../charts';
import { ChartConfiguration } from 'chart.js';
import { InstrumentDropdownInput } from './instrument-chart-dropdown/instrument-dropdown-input';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { instrumentCategoryColors } from '../../constants';
import { externalTooltipHandler } from '../custom-hover-tooltip';
import { lighterColor } from '../../functions';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'libero-instrument-chart-card',
  templateUrl: './instrument-chart-card.component.html',
  styleUrls: ['./instrument-chart-card.component.scss'],
  standalone: false
})
export class InstrumentChartCardComponent implements OnInit, OnChanges {
  @Input() public cardData?: InstrumentChartCardInput;

  public doughnutChartData?: ChartConfiguration['data'];
  public dropdownData: InstrumentDropdownInput [] = [];

  public doughnutChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
        position: 'nearest',
        external: (context) => externalTooltipHandler(context, 'instrumentChart'),
      },
    },
  };

  public showLabels = false;

  private datasetsLabels: string[] = [];
  private datasetsColors: string[] = [];

  public constructor(
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private translateService: TranslateService,
  ) { }

  public ngOnInit(): void {
    this.renderData();
  }

  private renderData(): void {
    if(!this.cardData?.chartData || this.cardData?.chartData.length < 1) {
      return;
    }

    const datasets = this.createDatasets();

    this.doughnutChartData = {
      labels: this.datasetsLabels,
    datasets: [{
      data: datasets,
      backgroundColor: this.datasetsColors,
      hoverBackgroundColor: [...this.datasetsColors.map(color => lighterColor(color, 50))],
      hoverBorderWidth: 0,
    }]};

    this.updateDropdownData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (JSON.stringify(changes['cardData'].previousValue) !== JSON.stringify(changes['cardData'].currentValue)) {
      this.renderData()
    }
  }

  public createDatasets(): number[] {
    // todo - dynamize this but keep labels colors paired with chart values!
    const equity = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.EQUITY && !instrument.instrument.cash).flatMap(inst => inst.weight);
    const bond = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.BOND && !instrument.instrument.cash).flatMap(inst => inst.weight);
    const alternative = this.cardData?.chartData.filter(instrument => (instrument.instrument.instrumentCategory === InstrumentCategories.ALTERNATIVE && !instrument.instrument.cash)).flatMap(inst => inst.weight);
    const comodity = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.COMODITY && !instrument.instrument.cash).flatMap(inst => inst.weight);
    const money = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.MONEY && !instrument.instrument.cash).flatMap(inst => inst.weight);
    const realEstate = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.REAL_ESTATE && !instrument.instrument.cash).flatMap(inst => inst.weight);
    const leveraged = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.LEVERAGED && !instrument.instrument.cash).flatMap(inst => inst.weight);
    const cryptocurrency = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.CRYPTOCURRENCY && !instrument.instrument.cash).flatMap(inst => inst.weight);
    const multiAsset = this.cardData?.chartData.filter(instrument => instrument.instrument.instrumentCategory === InstrumentCategories.MULTI_ASSET && !instrument.instrument.cash).flatMap(inst => inst.weight);

    const equitySum = equity?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const bondSum = bond?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const alternativeSum = alternative?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const comoditySum = comodity?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const moneySum = money?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const realEstateSum = realEstate?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const leveragedSum = leveraged?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const cryptocurrencySum = cryptocurrency?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;
    const multiAssetSum = multiAsset?.reduce((partialSum, a) => partialSum + a, 0) ?? 0;

    const cashWeight = this.cardData?.chartData.find(instrument => instrument.instrument.cash)!.weight ?? 0;

    this.datasetsLabels.length = 0;
    this.datasetsColors.length = 0;

    const colors: string[] = [];
    const dataSets: number[] = [];

    if(equitySum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.EQUITY'));
      colors.push(instrumentCategoryColors['EQUITY']);
      dataSets.push(parseInt(equitySum * 100 + ''));
    }
    if(bondSum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.BOND'));
      colors.push(instrumentCategoryColors['BOND']);
      dataSets.push(parseInt(bondSum * 100 + ''));
    }
    if(alternativeSum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.ALTERNATIVE'));
      colors.push(instrumentCategoryColors['ALTERNATIVE']);
      dataSets.push(parseInt(alternativeSum * 100 + ''));
    }
    if(comoditySum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.COMODITY'));
      colors.push(instrumentCategoryColors['COMODITY']);
      dataSets.push(parseInt(comoditySum * 100 + ''));
    }
    if(moneySum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.MONEY'));
      colors.push(instrumentCategoryColors['MONEY']);
      dataSets.push(parseInt(moneySum * 100 + ''));
    }
    if(realEstateSum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.REAL_ESTATE'));
      colors.push(instrumentCategoryColors['REAL_ESTATE']);
      dataSets.push(parseInt(realEstateSum * 100 + ''));
    }
    if(leveragedSum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.LEVERAGED'));
      colors.push(instrumentCategoryColors['LEVERAGED']);
      dataSets.push(parseInt(leveragedSum * 100 + ''));
    }
    if(cryptocurrencySum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.CRYPTOCURRENCY'));
      colors.push(instrumentCategoryColors['CRYPTOCURRENCY']);
      dataSets.push(parseInt(cryptocurrencySum * 100 + ''));
    }
    if(multiAssetSum > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.MULTI_ASSET'));
      colors.push(instrumentCategoryColors['MULTI_ASSET']);
      dataSets.push(parseInt(multiAssetSum * 100 + ''));
    }
    if(cashWeight > 0) {
      this.datasetsLabels.push(this.translateService.instant('investmentChartCategories.CASH'));
      colors.push(instrumentCategoryColors['CASH']);
      dataSets.push(parseInt(cashWeight * 100 + ''));
    }

    this.datasetsColors.push(...new Set(colors));
    return dataSets;
  }

  public updateDropdownData(): void {
    const dropdownData: InstrumentDropdownInput[] = [];
    this.dropdownData.length = 0;

    if (this.cardData) {
      const uniqueKeys = new Set(this.cardData.chartData.filter(instrument => !instrument.instrument.cash).map(instrument => instrument.instrument.instrumentCategory));
      uniqueKeys.add('CASH');

      for (const instrumentCategory of [...uniqueKeys]) {
        if(instrumentCategory === null || !instrumentCategory) {
          this.loggerService.error('Invalid state - instrument without category in ' + JSON.stringify(this.cardData.chartData));
          continue;
        }
        dropdownData.push(
          {
            categoryName: instrumentCategory,
            instrumentContent: this.getAllCategoryInstruments(instrumentCategory),
          }
        );
      }
    }

    this.dropdownData = [...dropdownData];
  }

  private getAllCategoryInstruments(categoryKey: keyof typeof InstrumentCategories) {
    if(!this.cardData) {
      return [];
    }

    const categoryInstruments = this.cardData.chartData.filter(instrument => {
      return instrument.instrument.instrumentCategory === categoryKey && !instrument.instrument.cash
    });

    if(categoryKey === 'CASH') {
      // add cash to money sublist
      categoryInstruments.push(this.cardData.chartData.find(instrument => instrument.instrument.cash)!);
    }

    return categoryInstruments;
  }

}
